import { useUsers, useRemoveUserFromTenant } from "@/api/user.api";
import { useTenantStore } from "@/store/tenant/tenant.store";
import { useTranslation } from "react-i18next";
import { usersTableConfig } from "../../users-table.config";
import { CardsDataTable } from "@/components/table-old/table";
import { Button } from "@/components/ui/button";
import { Plus, MoreHorizontal, Edit, Trash2 } from "lucide-react";
import { Outlet, useNavigate } from "react-router";
import { useUrlPagination } from "@/hooks/use-url-pagination";
import { useState } from "react";
import { SortingState, ColumnDef } from "@tanstack/react-table";
import { UserDto } from "@exibra/shared";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";

export const UsersTable = () => {
    const { t } = useTranslation();
    const selectedTenant = useTenantStore((state) => state.selectedTenant);
    const navigate = useNavigate();
    const [search, setSearch] = useState("");
    const [sorting, setSorting] = useState<SortingState>([]);

    // State for dialogs
    const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
    const [userToRemove, setUserToRemove] = useState<UserDto | undefined>();

    // Use URL-based pagination
    const { currentPage, pageSize, setPage, resetPage } = useUrlPagination({ pageSize: 10 });

    const { data: userData } = useUsers(selectedTenant?.id, {
        page: currentPage,
        pageSize: pageSize,
        search,
        sortingKey: sorting[0]?.id,
        sortingDirection: sorting[0]?.id ? (sorting[0]?.desc ? "desc" : "asc") : undefined,
    });
    const { mutate: removeUserFromTenant } = useRemoveUserFromTenant();

    const handleEditUser = (user: UserDto) => {
        navigate(`${user.id}/edit`);
    };

    const handleRemoveUser = (user: UserDto) => {
        setUserToRemove(user);
        setRemoveDialogOpen(true);
    };

    const confirmRemoveUser = () => {
        if (!selectedTenant || !userToRemove) return;

        removeUserFromTenant({
            tenantId: selectedTenant.id,
            userId: userToRemove.id,
        });

        setRemoveDialogOpen(false);
        setUserToRemove(undefined);
    };

    const cancelRemoveUser = () => {
        setRemoveDialogOpen(false);
        setUserToRemove(undefined);
    };

    // Enhanced columns with actions
    const columns: ColumnDef<UserDto>[] = [
        ...usersTableConfig,
        {
            id: "actions",
            header: "",
            size: 50,
            minSize: 50,
            cell: ({ row }) => {
                const user = row.original;

                return (
                    <div className="flex justify-end">
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" className="h-6 w-6 p-0">
                                    <span className="sr-only">{t("general.openMenu")}</span>
                                    <MoreHorizontal className="h-4 w-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleEditUser(user)}>
                                    <Edit className="mr-2 h-4 w-4" />
                                    <p className="text-xs font-medium">{t("general.edit")}</p>
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    onClick={() => handleRemoveUser(user)}
                                    className="text-destructive"
                                >
                                    <Trash2 className="mr-2 h-4 w-4" />
                                    <p className="text-xs font-medium">{t("general.delete")}</p>
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                );
            },
        },
    ];

    return (
        <>
            <CardsDataTable
                title={t("users.users.title")}
                description={t("users.users.description")}
                columns={columns}
                data={userData?.data ?? []}
                totalCount={userData?.totalCount ?? 0}
                currentPage={currentPage}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageReset={resetPage}
                search={search}
                onSearchChange={setSearch}
                sorting={sorting}
                onSortingChange={setSorting}
                actions={
                    <Button variant="outline" onClick={() => navigate("invite")}>
                        <Plus className="size-4" />
                        {t("users.invite")}
                    </Button>
                }
            />

            {/* Remove User Dialog */}
            <Dialog open={removeDialogOpen} onOpenChange={setRemoveDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{t("users.remove.title")}</DialogTitle>
                        <DialogDescription>
                            {userToRemove &&
                                t("users.remove.confirm", {
                                    name: userToRemove.name || userToRemove.email,
                                })}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={cancelRemoveUser}>
                            {t("general.cancel")}
                        </Button>
                        <Button variant="destructive" onClick={confirmRemoveUser}>
                            {t("users.remove.title")}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Outlet />
        </>
    );
};
